/**
 * Central password validation for signup, invite, reset, and change-password flows.
 *
 * Rules (UI checklist):
 *   1. Minimum length
 *   2. Uppercase letter
 *   3. Number or symbol
 *   4. Avoid predictable patterns (see pattern helpers below)
 *
 * Export map (where each public API is used):
 *   PASSWORD_* constants, PASSWORD_REQUIREMENT_UI — PasswordField, SignupStepOne
 *   getPasswordRequirementStatus — PasswordField, SignupStepOne
 *   getPasswordRequirementDisplayStatus — MyProfile (checklist + empty-state styling)
 *   getPasswordFieldError, validatePasswordPair, getPasswordChangeValidationErrors — SignupStepOne
 *   getPasswordEasyPatternError, getConfirmPasswordMismatchError, isNewPasswordSubmittable — ResetPassword, InviteUser
 *   validateChangePasswordForm, isChangePasswordSubmittable, isNewPasswordInputInvalid — MyProfile
 *   isStrongCustomPassword, meetsPasswordCompositionRules, isPasswordFullyValid — tests + composed checks
 */

// =============================================================================
// Constants — rules, regexes, and user-facing copy
// =============================================================================

export const PASSWORD_MIN_LENGTH = 8

/** Rule 2: at least one Latin uppercase letter. */
export const PASSWORD_HAS_UPPERCASE = /[A-Z]/

/** Rule 3: at least one digit or symbol (same set on every password screen). */
export const PASSWORD_HAS_NUMBER_OR_SYMBOL =
  /[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/

export const PASSWORD_EASY_PATTERNS_MESSAGE =
  "Avoid common words and patterns like 12345 or !@# (keyboard patterns such as qwerty)."

export const PASSWORD_REQUIRED_MESSAGE = "Password is required"
export const PASSWORD_CONFIRM_REQUIRED_MESSAGE = "Please confirm your password"
export const PASSWORD_TOO_SHORT_MESSAGE = `Password must be at least ${PASSWORD_MIN_LENGTH} characters`
export const PASSWORD_MISSING_UPPERCASE_MESSAGE =
  "Password must contain at least 1 uppercase character"
export const PASSWORD_MISSING_NUMBER_OR_SYMBOL_MESSAGE =
  "Password must contain at least 1 number or symbol"
export const PASSWORD_CONTAINS_NAME_MESSAGE = "Password cannot contain your name."
export const PASSWORD_MUST_DIFFER_FROM_CURRENT_MESSAGE =
  "New password must be different from your current password."
export const PASSWORD_MISMATCH_MESSAGE = "Passwords do not match"
export const PASSWORD_ALL_FIELDS_REQUIRED_MESSAGE = "All fields are required."
export const PASSWORD_CHANGE_FAILED_REQUIREMENTS_MESSAGE =
  "Password does not meet security requirements."

export const PASSWORD_REQUIREMENT_LABELS = {
  minLength: "At least 8 characters long",
  hasUppercase: "Contains 1 uppercase character",
  hasNumberOrSymbol: "Contains 1 number or symbol",
  avoidsEasyPatterns:
    "Avoids easy-to-guess words or patterns (e.g., 12345, qwerty)",
}

/** Ordered checklist rows for PasswordField and SignupStepOne. */
export const PASSWORD_REQUIREMENT_UI = [
  { key: "minLength", label: PASSWORD_REQUIREMENT_LABELS.minLength },
  { key: "hasUppercase", label: PASSWORD_REQUIREMENT_LABELS.hasUppercase },
  { key: "hasNumberOrSymbol", label: PASSWORD_REQUIREMENT_LABELS.hasNumberOrSymbol },
  { key: "avoidsEasyPatterns", label: PASSWORD_REQUIREMENT_LABELS.avoidsEasyPatterns },
]

const MIN_KEYBOARD_PATTERN_LENGTH = 4
const MAX_KEYBOARD_PATTERN_LENGTH = 8
const MIN_SEQUENTIAL_RUN_LENGTH = 4
const MIN_USER_NAME_PART_LENGTH = 3
const MIN_ADJACENT_DUPLICATE_RUN = 3

/**
 * Letter/digit keyboard rows only (no symbol rows). Patterns are precomputed at load
 * so runtime checks stay a simple substring search.
 */
const KEYBOARD_ROWS = [
  "qwertyuiop",
  "poiuytrewq",
  "asdfghjkl",
  "lkjhgfdsa",
  "zxcvbnm",
  "mnbvcxz",
  "1234567890",
  "0987654321",
]

function buildPracticalKeyboardPatterns() {
  const patterns = new Set()
  for (const row of KEYBOARD_ROWS) {
    for (let start = 0; start < row.length; start += 1) {
      const maxLength = Math.min(MAX_KEYBOARD_PATTERN_LENGTH, row.length - start)
      for (let length = MIN_KEYBOARD_PATTERN_LENGTH; length <= maxLength; length += 1) {
        patterns.add(row.slice(start, start + length))
      }
    }
  }
  return patterns
}

const PRACTICAL_KEYBOARD_PATTERNS = buildPracticalKeyboardPatterns()

// =============================================================================
// Rule 1–3: composition (length, uppercase, number/symbol)
// =============================================================================

function isNonEmptyString(value) {
  return typeof value === "string" && value.length > 0
}

/** Rule 1 — minimum character count. */
export function hasMinimumPasswordLength(password) {
  return isNonEmptyString(password) && password.length >= PASSWORD_MIN_LENGTH
}

/** Rule 2 — at least one uppercase letter. */
export function hasUppercaseLetter(password) {
  return isNonEmptyString(password) && PASSWORD_HAS_UPPERCASE.test(password)
}

/** Rule 3 — at least one digit or symbol. */
export function hasNumberOrSymbol(password) {
  return isNonEmptyString(password) && PASSWORD_HAS_NUMBER_OR_SYMBOL.test(password)
}

/** Rules 1–3 together (signup submit, full validity). */
export function meetsPasswordCompositionRules(password) {
  if (!isNonEmptyString(password)) return false
  return (
    hasMinimumPasswordLength(password) &&
    hasUppercaseLetter(password) &&
    hasNumberOrSymbol(password)
  )
}

// =============================================================================
// Rule 4: pattern / personal-info checks (no word blacklist)
// =============================================================================

function isAsciiDigitOrLowercaseLetter(charCode) {
  const isDigit = charCode >= 48 && charCode <= 57
  const isLowercase = charCode >= 97 && charCode <= 122
  return isDigit || isLowercase
}

/** Ascending or descending runs in one direction only (e.g. abcd, 4321), length ≥ 4. */
function hasLongSequentialAlphanumericRun(
  lowercasedPassword,
  minimumRunLength = MIN_SEQUENTIAL_RUN_LENGTH,
) {
  if (lowercasedPassword.length < minimumRunLength) return false

  let runLength = 1
  let lastDirection = 0

  for (let index = 1; index < lowercasedPassword.length; index += 1) {
    const previousCharCode = lowercasedPassword.charCodeAt(index - 1)
    const currentCharCode = lowercasedPassword.charCodeAt(index)

    if (!isAsciiDigitOrLowercaseLetter(previousCharCode) || !isAsciiDigitOrLowercaseLetter(currentCharCode)) {
      runLength = 1
      lastDirection = 0
      continue
    }

    let stepDirection = 0
    if (currentCharCode === previousCharCode + 1) stepDirection = 1
    else if (currentCharCode === previousCharCode - 1) stepDirection = -1

    if (stepDirection === 0) {
      runLength = 1
      lastDirection = 0
      continue
    }

    if (stepDirection === lastDirection) {
      runLength += 1
    } else {
      runLength = 2
      lastDirection = stepDirection
    }

    if (runLength >= minimumRunLength) return true
  }
  return false
}

/** Common keyboard-row substrings (qwerty, asdf, zxcv, 1234, etc.), length 4–8. */
function containsPracticalKeyboardPattern(lowercasedPassword) {
  if (lowercasedPassword.length < MIN_KEYBOARD_PATTERN_LENGTH) return false
  for (const pattern of PRACTICAL_KEYBOARD_PATTERNS) {
    if (lowercasedPassword.includes(pattern)) return true
  }
  return false
}

/** Entire password is one repeated character (e.g. aaaaaaaa, 11111111). */
function isSingleCharacterRepeatedThroughout(password) {
  return /^(\d)\1+$/.test(password) || /^([a-zA-Z])\1+$/.test(password)
}

/** Three or more identical characters in a row (e.g. aaa); allows normal doubles like "ll". */
function hasLongAdjacentDuplicateRun(
  password,
  minimumRunLength = MIN_ADJACENT_DUPLICATE_RUN,
) {
  const pattern = new RegExp(`(.)\\1{${minimumRunLength - 1},}`, "u")
  return pattern.test(password)
}

function parseNamePartsForPasswordCheck(fullName) {
  if (!isNonEmptyString(fullName)) return []
  return fullName
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((part) => part.length >= MIN_USER_NAME_PART_LENGTH)
}

/** Password contains a name token from the user's full name (3+ chars). */
function passwordContainsUserNamePart(lowercasedPassword, fullName) {
  const nameParts = parseNamePartsForPasswordCheck(fullName)
  return nameParts.some((part) => lowercasedPassword.includes(part))
}

function hasLeadingOrTrailingWhitespace(password) {
  return password.trim() !== password
}

function equalsCurrentPasswordIgnoreCase(password, currentPassword) {
  if (!currentPassword) return false
  return password.toLowerCase() === currentPassword.toLowerCase()
}

/**
 * Rule 4 — passes when password avoids predictable patterns and personal data.
 * Used by checklist "avoids easy patterns" and full validity.
 */
export const isStrongCustomPassword = (password, fullName = "", currentPassword = "") => {
  if (!isNonEmptyString(password)) return false
  if (hasLeadingOrTrailingWhitespace(password)) return false
  if (equalsCurrentPasswordIgnoreCase(password, currentPassword)) return false

  const lowercasedPassword = password.toLowerCase()

  if (isSingleCharacterRepeatedThroughout(password)) return false
  if (hasLongAdjacentDuplicateRun(password)) return false
  if (hasLongSequentialAlphanumericRun(lowercasedPassword)) return false
  if (containsPracticalKeyboardPattern(lowercasedPassword)) return false
  if (passwordContainsUserNamePart(lowercasedPassword, fullName)) return false

  return true
}

// =============================================================================
// Combined validity
// =============================================================================

/** All four rules (composition + patterns). */
export function isPasswordFullyValid(password, fullName = "", currentPassword = "") {
  return (
    meetsPasswordCompositionRules(password) &&
    isStrongCustomPassword(password, fullName, currentPassword)
  )
}

export function passwordsMatch(password, confirmPassword) {
  return password === confirmPassword
}

export function isExactSamePassword(newPassword, currentPassword) {
  return Boolean(newPassword && currentPassword && newPassword === currentPassword)
}

// =============================================================================
// UI checklist state (live feedback while typing)
// =============================================================================

/**
 * Boolean flags for each checklist row.
 * Used by: PasswordField, SignupStepOne.
 */
export function getPasswordRequirementStatus(password, fullName = "", currentPassword = "") {
  const value = password || ""
  return {
    minLength: hasMinimumPasswordLength(value),
    hasUppercase: hasUppercaseLetter(value),
    hasNumberOrSymbol: hasNumberOrSymbol(value),
    avoidsEasyPatterns: Boolean(value) && isStrongCustomPassword(value, fullName, currentPassword),
  }
}

/** All four checklist rules satisfied (confirm field not checked). */
export function isPasswordRequirementsMet(password, fullName = "", currentPassword = "") {
  const status = getPasswordRequirementStatus(password, fullName, currentPassword)
  return (
    status.minLength &&
    status.hasUppercase &&
    status.hasNumberOrSymbol &&
    status.avoidsEasyPatterns
  )
}

/**
 * My Profile modal: same rules as getPasswordRequirementStatus, but composition
 * rows stay unchecked until the user has typed something.
 */
export function getPasswordRequirementDisplayStatus(password, fullName = "", currentPassword = "") {
  const status = getPasswordRequirementStatus(password, fullName, currentPassword)
  const hasTypedValue = Boolean(password && password.length > 0)
  return {
    ...status,
    length: status.minLength,
    uppercase: hasTypedValue && status.hasUppercase,
    numberOrSymbol: hasTypedValue && status.hasNumberOrSymbol,
    avoidsEasyPatterns: hasTypedValue && status.avoidsEasyPatterns,
  }
}

// =============================================================================
// Field-level error messages
// =============================================================================

function getEasyPatternFailureMessage(password, fullName = "", currentPassword = "") {
  if (!password) return PASSWORD_EASY_PATTERNS_MESSAGE

  const lowercasedPassword = password.toLowerCase()
  if (equalsCurrentPasswordIgnoreCase(password, currentPassword)) {
    return PASSWORD_MUST_DIFFER_FROM_CURRENT_MESSAGE
  }
  if (passwordContainsUserNamePart(lowercasedPassword, fullName)) {
    return PASSWORD_CONTAINS_NAME_MESSAGE
  }
  return PASSWORD_EASY_PATTERNS_MESSAGE
}

/** First composition or pattern error for the password input, or null if OK. */
export function getPasswordFieldError(
  password,
  fullName = "",
  currentPassword = "",
  { required = false } = {},
) {
  const isEmpty = !password || !String(password).trim()
  if (isEmpty) {
    return required ? PASSWORD_REQUIRED_MESSAGE : null
  }

  if (!hasMinimumPasswordLength(password)) return PASSWORD_TOO_SHORT_MESSAGE
  if (!hasUppercaseLetter(password)) return PASSWORD_MISSING_UPPERCASE_MESSAGE
  if (!hasNumberOrSymbol(password)) return PASSWORD_MISSING_NUMBER_OR_SYMBOL_MESSAGE
  if (!isStrongCustomPassword(password, fullName, currentPassword)) {
    return getEasyPatternFailureMessage(password, fullName, currentPassword)
  }
  return null
}

/** Rule 4 only — for screens that already show the four-row checklist (reset, invite). */
export function getPasswordEasyPatternError(password, fullName = "", currentPassword = "") {
  if (!password) return ""
  const { avoidsEasyPatterns } = getPasswordRequirementStatus(password, fullName, currentPassword)
  if (avoidsEasyPatterns) return ""
  return getEasyPatternFailureMessage(password, fullName, currentPassword)
}

/** Confirm field when user typed confirm but values differ. */
export function getConfirmPasswordMismatchError(password, confirmPassword) {
  if (!confirmPassword) return null
  if (!passwordsMatch(password, confirmPassword)) return PASSWORD_MISMATCH_MESSAGE
  return null
}

// =============================================================================
// Form validation (signup and live typing)
// =============================================================================

/** Signup submit: errors keyed by `password` / `confirmPassword`. */
export function validatePasswordPair({
  password,
  confirmPassword,
  fullName = "",
  currentPwd = "",
  requireConfirm = true,
}) {
  const errors = {}

  const passwordError = getPasswordFieldError(password, fullName, currentPwd, { required: true })
  if (passwordError) errors.password = passwordError

  if (!requireConfirm) return errors

  const confirmIsEmpty = !confirmPassword || !String(confirmPassword).trim()
  if (confirmIsEmpty) {
    errors.confirmPassword = PASSWORD_CONFIRM_REQUIRED_MESSAGE
  } else if (!passwordsMatch(password, confirmPassword)) {
    errors.confirmPassword = PASSWORD_MISMATCH_MESSAGE
  }

  return errors
}

/** Signup while typing: optional confirm mismatch after password is valid. */
export function getPasswordChangeValidationErrors(
  password,
  fullName = "",
  confirmPassword = "",
  currentPassword = "",
) {
  const errors = {}
  const passwordError = getPasswordFieldError(password, fullName, currentPassword)
  if (passwordError) {
    errors.password = passwordError
    return errors
  }

  if (confirmPassword && !passwordsMatch(password, confirmPassword)) {
    errors.confirmPassword = PASSWORD_MISMATCH_MESSAGE
  }
  return errors
}

// =============================================================================
// Submit readiness (reset, invite, change password)
// =============================================================================

/** New password + confirm ready to submit (reset, invite; also part of change-password). */
export function isNewPasswordSubmittable(
  password,
  confirmPassword,
  fullName = "",
  currentPassword = "",
) {
  return (
    isPasswordFullyValid(password, fullName, currentPassword) &&
    Boolean(confirmPassword) &&
    passwordsMatch(password, confirmPassword)
  )
}

/** My Profile: highlight new password field when rules fail or same as current. */
export function isNewPasswordInputInvalid(newPassword, fullName = "", currentPassword = "") {
  if (!newPassword) return false
  return (
    !isPasswordRequirementsMet(newPassword, fullName, currentPassword) ||
    isExactSamePassword(newPassword, currentPassword)
  )
}

export function isChangePasswordSubmittable(
  currentPassword,
  newPassword,
  confirmPassword,
  fullName = "",
) {
  return (
    Boolean(currentPassword) &&
    isNewPasswordSubmittable(newPassword, confirmPassword, fullName, currentPassword)
  )
}

/** My Profile submit: first blocking message, or null when the form can be sent. */
export function validateChangePasswordForm({
  currentPassword,
  newPassword,
  confirmPassword,
  fullName = "",
}) {
  const allFieldsFilled = currentPassword && newPassword && confirmPassword
  if (!allFieldsFilled) {
    return PASSWORD_ALL_FIELDS_REQUIRED_MESSAGE
  }

  const mismatchError = getConfirmPasswordMismatchError(newPassword, confirmPassword)
  if (mismatchError) return mismatchError

  const newPasswordReady = isNewPasswordSubmittable(
    newPassword,
    confirmPassword,
    fullName,
    currentPassword,
  )
  if (!newPasswordReady) {
    return (
      getPasswordFieldError(newPassword, fullName, currentPassword) ||
      PASSWORD_CHANGE_FAILED_REQUIREMENTS_MESSAGE
    )
  }

  return null
}
